import { EndScreen } from './end-screen';
import { EndScreenAppPreview } from './end-screen-app-preview';
import { EndScreenDefault } from './end-screen-default';
import { EndScreenHtml } from './end-screen-html';
import { EndScreenImage } from './end-screen-image';
import { EndScreenVast } from './end-screen-vast';
import {
  EndScreenSkipType,
  EndScreenType,
  EndScreenSkipTypeValue
} from './end-screen-types';
import {
  sendEndScreenAutoClosed,
  sendEndScreenClicked,
  sendEndScreenClosed,
  sendEndScreenDisplayed,
  sendEndScreenSelected
} from '../../../../common/js/TrackAnalyticsHelper';
import { prefetchImages } from '../video-prefetch';
import { computed, signal, Signal } from '@preact/signals';
import VASTClient from '../vast/vast-client';
import envVars from '../../../../common/js/config';

interface OrchestratorContext {
  advertiserBundle?: string;
  deviceOs?: string;
}

interface InitParams {
  config: FormatConfig;
  context: OrchestratorContext;
  vastClient: VASTClient | null;
}

// TODO: Refactor EndScreenOrchestrator into a store => https://jira.ogury.io/browse/SPY-15284
export class EndScreenOrchestrator {
  static _instance?: EndScreenOrchestrator;
  static get instance(): EndScreenOrchestrator {
    if (!this._instance) {
      this._instance = new EndScreenOrchestrator();
    }

    return this._instance;
  }

  _currentIndex: Signal<number>;
  _endScreens: EndScreen[];
  config!: FormatConfig;

  constructor() {
    this._currentIndex = signal(-1);
    this._endScreens = [];
  }

  _endScreen = computed(() => {
    if (
      this._currentIndex.value >= 0 &&
      this._currentIndex.value < this._endScreens.length
    ) {
      return this._endScreens[this._currentIndex.value];
    }
  });

  get endScreen(): EndScreen | undefined {
    return this._endScreen.value;
  }

  get hasNext(): boolean {
    return this._currentIndex.value + 1 < this._endScreens.length;
  }

  init({ config, context, vastClient }: InitParams): Promise<void> {
    this.config = config;
    const endScreen = this._getFirstEndScreen({
      config,
      vastClient
    });

    if (!endScreen) return Promise.resolve();

    this._endScreens.push(endScreen);
    sendEndScreenSelected(endScreen);

    if (endScreen.type === EndScreenType.IMAGE) {
      prefetchImages([endScreen.content as string]);
    }

    return this._getAppPreviewEndScreen({ config, context }).then(
      appPreviewEndScreen => {
        if (!appPreviewEndScreen) return;

        this._endScreens.push(appPreviewEndScreen);
        sendEndScreenSelected(appPreviewEndScreen);
        prefetchImages([(appPreviewEndScreen.content as any).icon]);
      }
    );
  }

  _getFirstEndScreen({
    config,
    vastClient
  }: {
    config: FormatConfig;
    vastClient: VASTClient | null;
  }): EndScreen | null {
    const companionAd = vastClient && vastClient.getEndScreen();

    const endScreenClasses = [
      EndScreenVast,
      EndScreenImage,
      EndScreenHtml,
      EndScreenDefault
    ];

    for (const EndScreenClass of endScreenClasses) {
      const endScreen = new EndScreenClass({
        config: config as any,
        companionAd,
        vastClient
      } as any);

      const isAuthorized = this._isEndScreenAuthorizedByAdUnitType(endScreen);

      if (isAuthorized && endScreen.isValid()) {
        return endScreen;
      }
    }

    return null;
  }

  _getAppPreviewEndScreen({
    config,
    context
  }: {
    config: FormatConfig;
    context: OrchestratorContext;
  }): Promise<EndScreen | null> {
    const appBundle = context.advertiserBundle;
    const deviceOs = context.deviceOs;

    if (!appBundle || !deviceOs) {
      return Promise.resolve(null);
    }

    const baseUrl = `${envVars.MS_ADS_URL}/app-infos`;
    const queries = `appBundle=${appBundle}&deviceOs=${deviceOs}`;
    const url = `${baseUrl}?${queries}`;

    return fetch(url)
      .then(res => res.json())
      .then(({ appName, icon }: { appName: string; icon: string }) => {
        const content = { icon, name: appName };

        const endScreen = new EndScreenAppPreview({
          config: config as any,
          content
        });
        if (
          endScreen.isValid() &&
          this._isEndScreenAuthorizedByAdUnitType(endScreen)
        ) {
          return endScreen;
        }

        return null;
      })
      .catch(() => null);
  }

  _isEndScreenAuthorizedByAdUnitType(endScreen: EndScreen): boolean {
    const endScreenConfig = this.config.endScreen;
    return !!(
      endScreenConfig?.allowListType?.indexOf(endScreen.type) !== undefined &&
      endScreenConfig?.allowListType?.indexOf(endScreen.type) >= 0
    );
  }

  getClickUrl(): string | null {
    if (!this.endScreen) return null;

    sendEndScreenClicked(this.endScreen);
    return this.endScreen.getClickUrl();
  }

  skip(type: EndScreenSkipTypeValue): void {
    if (this.endScreen) {
      switch (type) {
        case EndScreenSkipType.AUTO:
          sendEndScreenAutoClosed(this.endScreen);
          break;
        case EndScreenSkipType.MANUAL:
          sendEndScreenClosed(this.endScreen);
          break;
        default:
          break;
      }
    }

    this._currentIndex.value++;
    if (this.endScreen) {
      sendEndScreenDisplayed(this.endScreen);
    }
  }
}
